export interface IE2EData {
  token: string;
  refreshToken: string;
}

/**
 * Class to manage E2E from within the frontend and tests.
 *
 * Automatically manages storage for e2e scenarios.
 *
 * Make sure to check {@link E2EData.isSet} before trying to access data.
 */
export class E2EData {
  private data?: IE2EData;

  /**
   * Returns the access token.
   *
   * Note: check {@link isSet} to verify that the token exists before calling this method.
   *
   * @throws Error if token is not set
   */
  public get token(): string {
    if (!this.isSet) {
      throw new Error('Token not set');
    }

    return this.data!.token;
  }

  /**
   * Returns the refresh token.
   *
   * Note: check {@link isSet} to verify that the token exists before calling this method.
   *
   * @throws Error if refresh token is not set
   */
  public get refreshToken(): string {
    if (!this.isSet) {
      throw new Error('Refresh token not set');
    }

    return this.data!.refreshToken;
  }

  /**
   * Set and persist E2E data for tests
   *
   * @param data Data to set
   */
  public set(data: IE2EData) {
    const e2eData: IE2EData = {
      token: data.token,
      refreshToken: data.refreshToken
    };

    this.data = e2eData;

    this.theWindow.localStorage.setItem('e2e_data', JSON.stringify(e2eData));
  }

  public get isSet(): boolean {
    if (!this.data) {
      return false;
    }

    return !!this.data.token && !!this.data.refreshToken;
  }

  constructor(
    private theWindow: Window,
    tryLoad: boolean = true
  ) {
    if (tryLoad) {
      this.tryloadDataFromStorage();
    }
  }

  private tryloadDataFromStorage() {
    const dataString = this.theWindow.localStorage.getItem('e2e_data');
    if (dataString) {
      const data = JSON.parse(dataString);
      if ('token' in data && 'refreshToken' in data) {
        this.data = data;
      } else {
        console.warn('e2e_data in localStorage is malformed, clearing it');
        this.theWindow.localStorage.removeItem('e2e_data');
        this.data = undefined;
      }
    }
  }
}
